"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { Check, Copy, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUserInfo } from "@/contexts/UserInfoContext";

const uiLog = createLogAdapter("components/common/EnhancedUserAccount.tsx");

interface EnhancedUserAccountProps {
	userid: string;
	showCopy?: boolean;
	className?: string;
	language?: "zh" | "en";
	enableUserInfo?: boolean; // 是否启用API查询
	fallbackFormat?: "short" | "medium" | "long"; // 降级格式
}

export default function EnhancedUserAccount({
	userid,
	showCopy = true,
	className = "",
	language = "zh",
	enableUserInfo = true,
	fallbackFormat = "short",
}: EnhancedUserAccountProps) {
	const [copied, setCopied] = useState(false);
	const [displayName, setDisplayName] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const { getUserInfo, getCachedUserInfo } = useUserInfo();

	const translations = {
		zh: {
			copied: "已复制",
			copyFailed: "复制失败",
		},
		en: {
			copied: "Copied",
			copyFailed: "Copy failed",
		},
	};

	const t = translations[language];

	// 格式化地址为不同长度
	const formatAddress = (addr: string, format: "short" | "medium" | "long") => {
		if (addr.length <= 12) return addr;

		const formatMap = {
			short: { start: 5, end: 3 },
			medium: { start: 6, end: 6 },
			long: { start: 8, end: 8 },
		};

		const { start, end } = formatMap[format];
		return `${addr.slice(0, start)}...${addr.slice(-end)}`;
	};

	// 初始化显示名称
	useEffect(() => {
		// 首先设置格式化的userid
		setDisplayName(formatAddress(userid, fallbackFormat));

		// 如果启用了用户信息查询
		if (enableUserInfo) {
			// 检查缓存
			const cached = getCachedUserInfo(userid);
			if (cached && cached.username) {
				setDisplayName(cached.username);
				return;
			}

			// 异步加载用户信息
			setIsLoading(true);
			getUserInfo(userid)
				.then((userInfo) => {
					if (userInfo.username) {
						setDisplayName(userInfo.username);
					}
				})
				.catch((error) => {
					uiLog.error("Failed to load user info:", error);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [userid, enableUserInfo, fallbackFormat, getUserInfo, getCachedUserInfo]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(userid);
			setCopied(true);
			toast.success(t.copied);
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			uiLog.error("Failed to copy:", error);
			toast.error(t.copyFailed);
		}
	};

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="inline-flex items-center gap-1">
						{isLoading && (
							<Loader2 className="w-3 h-3 animate-spin text-light-text/40 dark:text-dark-text/40" />
						)}
						<span
							className={`font-mono text-sm ${className || "text-light-text dark:text-dark-text"}`}
						>
							{displayName}
						</span>
						{showCopy && (
							<Button
								variant="ghost"
								size="sm"
								className="h-6 w-6 p-0 hover:bg-light-surface/50 dark:hover:bg-dark-surface/50"
								onClick={handleCopy}
							>
								{copied ? (
									<Check className="w-3 h-3 text-green-500" />
								) : (
									<Copy className="w-3 h-3 text-light-text/60 dark:text-dark-text/60" />
								)}
							</Button>
						)}
					</div>
				</TooltipTrigger>
				<TooltipContent>
					<p className="font-mono text-xs">{userid}</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
